import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin,
  ShieldCheck,
  Sparkles,
  HeartHandshake,
  Mail,
  Phone,
  MessageSquare,
  Send,
  CheckCircle2,
  Clock,
  Building2,
  KeyRound,
  LifeBuoy,
  Megaphone,
  Car,
  ArrowRight,
  Copy,
  Search,
  CalendarClock,
  FileCheck2,
  KeySquare,
  Zap,
  Users2,
  Pause,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API, useAuth } from "@/context/AuthContext";

const STEPS = [
  {
    key: "browse",
    label: "Browse",
    icon: Search,
    accent: "Discover",
    headline: "Find the vehicle that fits the moment.",
    body: "Sedans, SUVs, sport cars, business-ready electric vehicles, trucks, and rideshare-ready cars — filter by category, seats, and daily price. Every listing shows transparent standard and Drive Alfa Club member pricing side-by-side.",
    highlights: [
      "Curated Ontario-ready fleet",
      "Transparent standard vs. member rates",
      "Filter by category, seats, and price",
    ],
    cta: { to: "/vehicles", label: "Browse the fleet" },
    stat: { value: "12+", label: "vehicle categories" },
  },
  {
    key: "reserve",
    label: "Reserve",
    icon: CalendarClock,
    accent: "Plan",
    headline: "Live pricing. Any duration you choose.",
    body: "Our interactive Booking Planner lets you pick presets or set an exact pick-up and return. We automatically break your rental into the cheapest combination of months + weeks + days — so a 9-day rental becomes 1 week + 2 days, never 9 days at the daily rate.",
    highlights: [
      "Optimal price breakdown, live",
      "Members unlock hourly and 1–6 day rentals",
      "Rental Term Leasing available (3, 6, or 12 months)",
    ],
    cta: { to: "/vehicles", label: "Plan a rental" },
    stat: { value: "1 hr", label: "member minimum booking" },
  },
  {
    key: "verify",
    label: "Verify",
    icon: FileCheck2,
    accent: "Confirm",
    headline: "Quick, secure, and low-friction.",
    body: "Upload your driver’s licence and required documents right from your Customer Portal. No traditional credit checks for standard rentals — approval is based on identity verification, insurance eligibility, and vehicle availability.",
    highlights: [
      "Secure document upload",
      "Deposit-based approval for longer terms",
      "Personal insurance accepted (subject to approval)",
    ],
    cta: { to: "/signup", label: "Create your account" },
    stat: { value: "< 24h", label: "typical approval" },
  },
  {
    key: "drive",
    label: "Drive",
    icon: KeySquare,
    accent: "Enjoy",
    headline: "Pick up, or we deliver across Ontario.",
    body: "Choose contact-light pick-up or opt-in delivery. Every rental is fully insured with human support one call away. Extend with a click, and enjoy up to 2 free rental days per month as a Drive Alfa Club member.",
    highlights: [
      "Ontario-wide operation — GTA, Ottawa, Hamilton, Windsor",
      "Human support, always",
      "Members get 2 free rental days each month",
    ],
    cta: { to: "/drive-alfa-club", label: "See member perks" },
    stat: { value: "100%", label: "insured fleet" },
  },
];

const VALUES = [
  { icon: Sparkles, t: "Curated Fleet", d: "From premium SUVs and sleek sedans to bold coupes and modern EVs, AlfaDrive gives you access to vehicles worth getting excited about.", tint: "blue" },
  { icon: ShieldCheck, t: "Drive with Confidence", d: "From booking to return, AlfaDrive gives you a smoother rental experience built around confidence, convenience, and 24/7/365 support.", tint: "dark" },
  { icon: MapPin, t: "Ontario-Wide Access", d: "From daily drives and weekend plans to business needs, AlfaDrive gives you flexible vehicle access across Ontario — ready when you are.", tint: "blue" },
  { icon: HeartHandshake, t: "Human support", d: "Real people. Real help. One call, one email, one message away.", tint: "dark" },
];

const TOPICS = [
  { key: "general", label: "General inquiry", icon: MessageSquare, blurb: "Anything you’d like to ask." },
  { key: "rentals", label: "Rentals", icon: Car, blurb: "Questions about booking a specific vehicle." },
  { key: "membership", label: "Drive Alfa Club", icon: KeyRound, blurb: "Membership, Rental Term Leasing, or perks." },
  { key: "business", label: "Alfa One Path", icon: Building2, blurb: "Business and fleet-only bookings." },
  { key: "support", label: "Support", icon: LifeBuoy, blurb: "Existing booking or account help." },
  { key: "press", label: "Press", icon: Megaphone, blurb: "Media, partnerships, brand." },
];

const QUICK_FACTS = [
  { icon: Clock, k: "Response", v: "< 20 min" },
  { icon: MapPin, k: "Coverage", v: "Ontario-wide" },
  { icon: ShieldCheck, k: "Support", v: "Real humans" },
];

const copyText = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy — please copy manually");
  }
};

const STEP_MS = 6500;

export default function AboutPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [tick, setTick] = useState(0); // 0..100 for progress
  const intervalRef = useRef(null);

  // Scroll to hash target if present
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash]);

  // Autoplay for the stepper
  useEffect(() => {
    if (!autoPlay) return;
    setTick(0);
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / STEP_MS) * 100);
      setTick(pct);
      if (elapsed >= STEP_MS) {
        setActiveStep((s) => (s + 1) % STEPS.length);
      }
    }, 80);
    return () => clearInterval(intervalRef.current);
  }, [autoPlay, activeStep]);

  const step = STEPS[activeStep];

  const goTo = (i) => {
    setActiveStep(i);
    setTick(0);
  };

  // Prefill for signed-in users only (first name + email + phone). Anonymous users get an empty form.
  const initialFormValues = () => ({
    full_name: user?.full_name ? user.full_name.split(" ")[0] : "",
    email: user?.email || "",
    phone: user?.phone || "",
    subject: "",
    message: "",
  });

  // Contact form state
  const [topic, setTopic] = useState("general");
  const [form, setForm] = useState(initialFormValues);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null);

  const activeTopic = TOPICS.find((t) => t.key === topic) || TOPICS[0];
  const canSubmit =
    form.full_name.trim().length >= 2 &&
    /.+@.+\..+/.test(form.email) &&
    form.message.trim().length >= 5 &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        topic,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      setSent(res.data);
      toast.success("Message sent — we’ll be in touch shortly");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSent(null);
    setForm(initialFormValues());
    setTopic("general");
  };

  return (
    <div className="bg-background" data-testid="about-page">
      {/* Hero */}
      <section className="hero-wash border-b border-border">
        <div className="container-x pt-16 sm:pt-20 pb-12 sm:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Ontario, Canada
              </div>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
                Mobility, redefined for <span className="text-[hsl(var(--primary))]">Ontario.</span>
              </h1>
              <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
                AlfaDrive is a modern vehicle rental company built for individuals, entrepreneurs, and businesses on the move. Access should feel as good as ownership — without the friction of long-term contracts.
              </p>
              <p className="mt-4 font-display tracking-widest text-sm text-muted-foreground uppercase">
                <span>Dare to</span> <span className="text-[hsl(var(--danger))] font-semibold">Live</span>.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]">
                  <Link to="/vehicles">Browse the fleet <ArrowRight className="h-4 w-4 ml-1" /></Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xl">
                  <a href="#how-it-works">See how it works</a>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: "Ontario", v: "Wide coverage" },
                  { k: "< 1 min", v: "Signup" },
                  { k: "1 hour", v: "Member minimum" },
                  { k: "12 months", v: "Longest term" },
                ].map((s) => (
                  <div key={s.k} className="card-clean p-4">
                    <div className="font-display text-2xl font-semibold tabular-nums text-foreground">{s.k}</div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive How It Works */}
      <section id="how-it-works" className="section-y bg-white" data-testid="how-it-works-section">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">How it works</div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-1 tracking-tight">Reserve in minutes. Drive with confidence.</h2>
              <p className="mt-3 text-muted-foreground">Four steps. Live pricing. Real support. Click through the flow — or let it play.</p>
            </div>
            <button
              type="button"
              onClick={() => setAutoPlay((v) => !v)}
              className="chip inline-flex items-center gap-2"
              data-testid="how-it-works-autoplay-toggle"
              aria-label={autoPlay ? "Pause tour" : "Play tour"}
            >
              {autoPlay ? <><Pause className="h-3.5 w-3.5" /> Pause tour</> : <><Play className="h-3.5 w-3.5" /> Play tour</>}
            </button>
          </div>

          <div className="mt-8 grid lg:grid-cols-12 gap-6">
            {/* Stepper */}
            <div className="lg:col-span-5" data-testid="how-it-works-stepper">
              <ol className="space-y-3">
                {STEPS.map((s, i) => {
                  const isActive = i === activeStep;
                  const isDone = i < activeStep;
                  return (
                    <li key={s.key}>
                      <button
                        type="button"
                        onClick={() => goTo(i)}
                        className={`w-full text-left rounded-2xl border transition-all duration-200 p-4 sm:p-5 flex items-start gap-4 ${
                          isActive
                            ? "border-[hsl(var(--blue-400))] bg-[hsl(var(--blue-100))]/60 shadow-[0_1px_0_hsla(220,20%,10%,0.05),0_12px_28px_hsla(220,20%,10%,0.06)]"
                            : "border-border bg-white hover:bg-secondary"
                        }`}
                        data-testid={`step-${s.key}`}
                        aria-current={isActive ? "step" : undefined}
                      >
                        <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                          isActive
                            ? "bg-[hsl(var(--primary))] text-white"
                            : isDone
                              ? "bg-[hsl(var(--charcoal-950))] text-white"
                              : "bg-secondary text-muted-foreground"
                        }`}>
                          <s.icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-[11px] tabular-nums text-muted-foreground">0{i + 1}</span>
                              <span className="font-display font-semibold truncate">{s.label}</span>
                            </div>
                            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.accent}</span>
                          </div>
                          {isActive && (
                            <div className="mt-2 h-1 rounded-full bg-white/60 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-[hsl(var(--primary))] transition-[width] duration-100 ease-linear"
                                style={{ width: `${autoPlay ? tick : 100}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* Panel */}
            <div className="lg:col-span-7" data-testid="how-it-works-panel">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step.key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
                  className="rounded-3xl border border-border bg-white overflow-hidden relative"
                  style={{ boxShadow: "0 1px 0 hsla(220,20%,10%,0.05), 0 12px 34px hsla(220,20%,10%,0.06)" }}
                >
                  <div className="relative">
                    <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
                      background: `radial-gradient(600px circle at 15% 15%, hsl(var(--primary)), transparent 55%)`,
                    }} />
                    <div className="relative p-6 sm:p-8">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--charcoal-950))] text-white flex items-center justify-center">
                          <step.icon className="h-6 w-6" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{step.accent}</div>
                          <div className="font-display text-xl sm:text-2xl font-semibold">Step {activeStep + 1} · {step.label}</div>
                        </div>
                      </div>

                      <h3 className="mt-5 font-display text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">{step.headline}</h3>
                      <p className="mt-3 text-muted-foreground text-sm sm:text-base leading-relaxed">{step.body}</p>

                      <ul className="mt-5 space-y-2">
                        {step.highlights.map((h) => (
                          <li key={h} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Button asChild className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]">
                          <Link to={step.cta.to} data-testid={`step-cta-${step.key}`}>{step.cta.label} <ArrowRight className="h-4 w-4 ml-1" /></Link>
                        </Button>
                        <div className="rounded-xl border border-border px-4 py-2 flex items-center gap-3 bg-secondary/60">
                          <div>
                            <div className="font-display text-2xl font-semibold tabular-nums">{step.stat.value}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{step.stat.label}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* What makes AlfaDrive different */}
      <section className="section-y bg-[hsl(var(--charcoal-100))]">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">What makes AlfaDrive different</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-1 tracking-tight">
              <span className="text-[hsl(var(--primary))]">Premium access. Flexible terms. A better way to drive.</span>
            </h2>
            <p className="mt-3 text-muted-foreground">AlfaDrive gives you a modern rental experience built around choice, convenience, and vehicles you actually want to drive.</p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.t}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.25, delay: i * 0.04 }}
                className={`relative rounded-2xl border border-border p-6 overflow-hidden group ${
                  v.tint === "dark"
                    ? "bg-[hsl(var(--charcoal-950))] text-white"
                    : "bg-white"
                }`}
              >
                <div className={`absolute inset-0 opacity-[0.6] pointer-events-none`} style={{
                  background: v.tint === "dark"
                    ? "radial-gradient(400px circle at 100% 100%, hsla(220,90%,55%,0.3), transparent 55%)"
                    : "radial-gradient(400px circle at 100% 100%, hsla(220,90%,55%,0.08), transparent 55%)",
                }} />
                <div className="relative">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${v.tint === "dark" ? "bg-white/10" : "bg-[hsl(var(--blue-100))]"}`}>
                    <v.icon className={`h-5 w-5 ${v.tint === "dark" ? "text-white" : "text-[hsl(var(--primary))]"}`} />
                  </div>
                  <div className="mt-4 font-display text-lg font-semibold">{v.t}</div>
                  <div className={`text-sm mt-1 ${v.tint === "dark" ? "text-white/70" : "text-muted-foreground"}`}>{v.d}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Two paths: Consumer vs Business */}
      <section className="section-y bg-white">
        <div className="container-x">
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="relative rounded-3xl border border-border bg-white overflow-hidden p-8 group">
              <div className="absolute inset-0 opacity-100 pointer-events-none" style={{
                background: "radial-gradient(500px circle at 20% 0%, hsla(220,90%,55%,0.12), transparent 55%)",
              }} />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs text-muted-foreground">
                  <KeyRound className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> For individuals
                </div>
                <h3 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Drive Alfa Club</h3>
                <p className="mt-3 text-muted-foreground">Unlock hourly and 1–6 day rentals, member pricing, Rental Term Leasing (3, 6, or 12 months), a full year of CAA, and lifestyle perks. $1,000 / year.</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {["Hourly & short-term rentals", "Member pricing on every rental", "2 free rental days each month"].map((l) => (
                    <li key={l} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /> {l}</li>
                  ))}
                </ul>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button asChild className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]">
                    <Link to="/drive-alfa-club">Explore Drive Alfa Club</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to="/vehicles">See vehicles</Link>
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative rounded-3xl border border-border bg-[hsl(var(--charcoal-950))] text-white overflow-hidden p-8 group">
              <div className="absolute inset-0 opacity-100 pointer-events-none" style={{
                background: "radial-gradient(500px circle at 80% 100%, hsla(220,90%,55%,0.35), transparent 55%)",
              }} />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/70">
                  <Building2 className="h-3.5 w-3.5" /> For businesses
                </div>
                <h3 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Alfa One Path</h3>
                <p className="mt-3 text-white/70">A dedicated business/fleet booking program for contractors, rideshare operators, sole proprietors, and corporations — with tailored terms, dedicated support, and fleet-friendly pricing.</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {["1 to 100+ vehicle fleets", "Dedicated business contact", "Rideshare documentation support"].map((l) => (
                    <li key={l} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--blue-400))] mt-0.5" /> {l}</li>
                  ))}
                </ul>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button asChild className="rounded-xl bg-white text-[hsl(var(--charcoal-950))] hover:bg-white/90">
                    <Link to="/alfa-one-path">Explore Alfa One Path</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us */}
      <section id="contact" className="section-y bg-[hsl(var(--charcoal-100))]" data-testid="contact-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs text-muted-foreground">
                <MessageSquare className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Contact us
              </div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                Talk to a real person.<br />
                <span className="text-[hsl(var(--primary))]">Emails answered in 20 minutes or less.</span>
              </h2>
              <p className="mt-3 text-muted-foreground max-w-md">
                Every email is answered by a real human — usually in 20 minutes or less. If it’s faster to talk, we’ll call you back. Our 24/7 customer agent phone line is getting set up and will be live soon.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => copyText("admin@alfadrive.ca", "Email")}
                  className="group card-clean p-4 flex items-center justify-between text-left transition-shadow"
                  data-testid="contact-copy-email"
                >
                  <span className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-lg bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] flex items-center justify-center"><Mail className="h-4 w-4" /></span>
                    <span>
                      <span className="text-[11px] uppercase tracking-widest text-muted-foreground block">Email · replies in 20 min or less</span>
                      <span className="font-medium">admin@alfadrive.ca</span>
                    </span>
                  </span>
                  <Copy className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
                <div className="card-clean p-4 flex items-center gap-3" data-testid="contact-phone-soon">
                  <span className="h-9 w-9 rounded-lg bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] flex items-center justify-center"><Phone className="h-4 w-4" /></span>
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Phone · 24/7 agent line</div>
                    <div className="font-medium">Coming online soon — we’ll call you back if needed</div>
                  </div>
                </div>
                <div className="card-clean p-4 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] flex items-center justify-center"><MapPin className="h-4 w-4" /></span>
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Location</div>
                    <div className="font-medium">Ontario, Canada</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                {QUICK_FACTS.map((q) => (
                  <div key={q.k} className="card-clean p-3">
                    <q.icon className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <div className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground">{q.k}</div>
                    <div className="text-sm font-medium leading-tight mt-0.5">{q.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="card-clean p-6 sm:p-8" data-testid="contact-card">
                <AnimatePresence mode="wait">
                  {sent ? (
                    <motion.div
                      key="sent"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                      className="text-center py-10"
                      data-testid="contact-success"
                    >
                      <div className="mx-auto h-14 w-14 rounded-full bg-[hsl(var(--blue-100))] flex items-center justify-center">
                        <CheckCircle2 className="h-7 w-7 text-[hsl(var(--primary))]" />
                      </div>
                      <h3 className="mt-4 font-display text-2xl font-semibold">Message received</h3>
                      <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                        Thanks, {form.full_name.split(" ")[0] || "there"}. We’ll follow up at
                        <span className="font-medium text-foreground"> {form.email}</span> — usually in 20 minutes or less.
                      </p>
                      <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <Button onClick={reset} className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="contact-send-another">Send another message</Button>
                        <Button asChild variant="outline" className="rounded-xl">
                          <Link to="/vehicles">Browse the fleet <ArrowRight className="h-4 w-4 ml-1" /></Link>
                        </Button>
                      </div>
                      <p className="mt-4 text-[11px] text-muted-foreground">Reference: <span className="font-mono">{sent.id.slice(0, 8)}</span></p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                      onSubmit={submit}
                      data-testid="contact-form"
                    >
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Pick a topic</div>
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="contact-topic-chips">
                        {TOPICS.map((t) => {
                          const isActive = topic === t.key;
                          return (
                            <button
                              type="button"
                              key={t.key}
                              onClick={() => setTopic(t.key)}
                              className={`chip ${isActive ? "chip-active" : ""}`}
                              data-testid={`contact-topic-${t.key}`}
                            >
                              <t.icon className="h-3.5 w-3.5" />{t.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground" data-testid="contact-topic-blurb">{activeTopic.blurb}</div>

                      <div className="mt-5 grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact-name">Full name</Label>
                          <Input id="contact-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Alex Morgan" data-testid="contact-name-input" />
                        </div>
                        <div>
                          <Label htmlFor="contact-email">Email</Label>
                          <Input id="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="contact-email-input" />
                        </div>
                        <div>
                          <Label htmlFor="contact-phone">Phone (optional)</Label>
                          <Input id="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (416) 555-0000" data-testid="contact-phone-input" />
                        </div>
                        <div>
                          <Label htmlFor="contact-subject">Subject (optional)</Label>
                          <Input id="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. 6-month Rental Term Leasing" data-testid="contact-subject-input" />
                        </div>
                      </div>

                      <div className="mt-4">
                        <Label htmlFor="contact-message">Message</Label>
                        <Textarea id="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us what you’re looking for…" data-testid="contact-message-input" maxLength={4000} />
                        <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                          <span>We’ll only use your info to reply to this message.</span>
                          <span className="tabular-nums" data-testid="contact-message-count">{form.message.length}/4000</span>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                          <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                          Your details stay private. Emails answered in 20 minutes or less by a real human.
                        </div>
                        <Button type="submit" disabled={!canSubmit} className="rounded-xl h-11 px-5 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="contact-submit-button">
                          {submitting ? (
                            <span className="inline-flex items-center">
                              <span className="h-2 w-2 rounded-full bg-white/80 mr-2 animate-pulse" />
                              Sending…
                            </span>
                          ) : (
                            <span className="inline-flex items-center">
                              <Send className="h-4 w-4 mr-1.5" /> Send message
                            </span>
                          )}
                        </Button>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
